import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Input, Modal, message as antdMessage } from "antd";
import { DislikeFilled, DislikeOutlined, LikeFilled, LikeOutlined } from "@ant-design/icons";

import { submitFacilityAiFeedback } from "./api";
import { feedbackMessageId } from "./feedback.mjs";


const ISSUE_OPTIONS = [
  ["incorrect_data", "Incorrect or stale data"],
  ["misunderstood", "Misunderstood my question"],
  ["missing_information", "Missing important information"],
  ["citation_problem", "Sources do not support the answer"],
  ["unclear", "Unclear or poorly written"],
  ["unsafe_action", "Unsafe or inappropriate action"],
  ["other", "Other"],
];


const AnswerFeedback = ({ item, companyId, facilityId }) => {
  const messageId = feedbackMessageId(item);
  const canSubmit = messageId !== null && item.status === "done";
  const [feedback, setFeedback] = useState(item.feedback || null);
  const [modalOpen, setModalOpen] = useState(false);
  const [rating, setRating] = useState(item.feedback?.rating || "not_helpful");
  const [issueCodes, setIssueCodes] = useState(item.feedback?.issue_codes || []);
  const [comment, setComment] = useState(item.feedback?.comment || "");
  const [suggestedAnswer, setSuggestedAnswer] = useState(
    item.feedback?.suggested_answer || ""
  );
  const [trainingConsent, setTrainingConsent] = useState(
    Boolean(item.feedback?.training_consent)
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setFeedback(item.feedback || null);
    setRating(item.feedback?.rating || "not_helpful");
    setIssueCodes(item.feedback?.issue_codes || []);
    setComment(item.feedback?.comment || "");
    setSuggestedAnswer(item.feedback?.suggested_answer || "");
    setTrainingConsent(Boolean(item.feedback?.training_consent));
  }, [item.feedback, item.content_markdown]);

  const issueLabels = useMemo(
    () => new Map(ISSUE_OPTIONS),
    []
  );

  const submit = async (payload) => {
    if (!canSubmit) return null;
    setSubmitting(true);
    try {
      const result = await submitFacilityAiFeedback({
        companyId,
        facilityId,
        messageId,
        payload,
      });
      setFeedback(result);
      return result;
    } catch (error) {
      antdMessage.error(error?.message || "Unable to submit feedback.");
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const submitHelpful = async () => {
    const result = await submit({
      rating: "helpful",
      issue_codes: [],
      comment: feedback?.comment || "",
      suggested_answer: feedback?.suggested_answer || "",
      training_consent: Boolean(feedback?.training_consent),
    });
    if (result) antdMessage.success("Thanks for your feedback.");
  };

  const openDetails = (nextRating) => {
    setRating(nextRating);
    if (nextRating === "helpful") setIssueCodes([]);
    setModalOpen(true);
  };

  const toggleIssue = (code) => {
    setIssueCodes((current) =>
      current.includes(code)
        ? current.filter((itemCode) => itemCode !== code)
        : [...current, code]
    );
  };

  const submitDetails = async () => {
    if (rating === "not_helpful" && issueCodes.length === 0) {
      antdMessage.info("Select at least one reason.");
      return;
    }
    const result = await submit({
      rating,
      issue_codes: rating === "not_helpful" ? issueCodes : [],
      comment: comment.trim(),
      suggested_answer: suggestedAnswer.trim(),
      training_consent: trainingConsent,
    });
    if (result) {
      setModalOpen(false);
      antdMessage.success("Feedback saved.");
    }
  };

  if (!canSubmit) return null;

  return (
    <div className="facility-ai-answer-feedback" aria-label="Answer feedback">
      <span className="facility-ai-answer-feedback-label">
        {feedback ? "Feedback saved" : "Was this helpful?"}
      </span>
      <button
        type="button"
        className={`facility-ai-feedback-icon${feedback?.rating === "helpful" ? " is-active" : ""}`}
        onClick={submitHelpful}
        disabled={submitting}
        aria-label="Mark answer as helpful"
        aria-pressed={feedback?.rating === "helpful"}
      >
        {feedback?.rating === "helpful" ? <LikeFilled /> : <LikeOutlined />}
      </button>
      <button
        type="button"
        className={`facility-ai-feedback-icon${feedback?.rating === "not_helpful" ? " is-active" : ""}`}
        onClick={() => openDetails("not_helpful")}
        disabled={submitting}
        aria-label="Mark answer as not helpful"
        aria-pressed={feedback?.rating === "not_helpful"}
      >
        {feedback?.rating === "not_helpful" ? <DislikeFilled /> : <DislikeOutlined />}
      </button>
      {feedback ? (
        <button
          type="button"
          className="facility-ai-feedback-details-link"
          onClick={() => openDetails(feedback.rating)}
        >
          Edit details
        </button>
      ) : null}

      <Modal
        title={rating === "helpful" ? "What worked well?" : "Help us improve this answer"}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)} disabled={submitting}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={submitDetails} loading={submitting}>
            Save feedback
          </Button>,
        ]}
        destroyOnClose={false}
      >
        {rating === "not_helpful" ? (
          <div className="facility-ai-feedback-field">
            <label>What was wrong?</label>
            <div className="facility-ai-feedback-issues">
              {ISSUE_OPTIONS.map(([code, label]) => (
                <button
                  key={code}
                  type="button"
                  className={issueCodes.includes(code) ? "is-selected" : ""}
                  onClick={() => toggleIssue(code)}
                  aria-pressed={issueCodes.includes(code)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="facility-ai-feedback-field">
          <label htmlFor={`feedback-comment-${messageId}`}>Additional comments</label>
          <Input.TextArea
            id={`feedback-comment-${messageId}`}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            rows={3}
            maxLength={4000}
            placeholder={rating === "helpful" ? "Tell us what was useful." : "Describe the problem."}
          />
        </div>
        <div className="facility-ai-feedback-field">
          <label htmlFor={`feedback-suggestion-${messageId}`}>Suggested answer or improvement</label>
          <Input.TextArea
            id={`feedback-suggestion-${messageId}`}
            value={suggestedAnswer}
            onChange={(event) => setSuggestedAnswer(event.target.value)}
            rows={4}
            maxLength={12000}
            placeholder="Optional: show us what a better answer would say."
          />
        </div>
        <Checkbox
          checked={trainingConsent}
          onChange={(event) => setTrainingConsent(event.target.checked)}
        >
          Allow the de-identified question, answer, and feedback to be reviewed for model improvement.
        </Checkbox>
        {feedback?.status && feedback.status !== "new" ? (
          <p className="facility-ai-feedback-review-status">
            Review status: {feedback.status.replace(/_/g, " ")}
          </p>
        ) : null}
        {feedback?.issue_codes?.length ? (
          <span className="facility-ai-feedback-existing-issues">
            {feedback.issue_codes.map((code) => issueLabels.get(code) || code).join(" · ")}
          </span>
        ) : null}
      </Modal>
    </div>
  );
};


export default AnswerFeedback;
